import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { REACT_APP_GLOBAL_API_BASE_URL_V2 } from 'runtimeEnvVars';

import {
  fetchApplicantDetailsInit,
  setApplicantDetail,
  updateApplicantTableStatuses,
} from 'containers/ApplicantTableV2/actions';
import { addErrorMessageFromCaughtValueI18n } from 'containers/FlashMessage/actions';
import { apiGet } from 'utils/axios';

export const fetchApplicantData = (applicantId, onErrorStatus) => {
  const url = `${REACT_APP_GLOBAL_API_BASE_URL_V2}/applicants/${applicantId}`;
  return onErrorStatus
    ? apiGet(url, undefined, { onErrorStatus })
    : apiGet(url);
};

export default function useApplicantDetail(
  applicantId,
  defaultFetching = true,
  { onSettled } = {},
) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [data, setData] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState('');
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  const fetchData = useCallback(() => {
    const reportSettled = onSettledRef.current;
    let outcome = 'failed';
    dispatch(fetchApplicantDetailsInit({ applicantId }));
    dispatch(
      setApplicantDetail({ info: { id: applicantId }, isFetching: true }),
    );
    fetchApplicantData(
      applicantId,
      reportSettled
        ? status => {
            if (status === 403 || status === 404) outcome = 'unavailable';
          }
        : undefined,
    )
      .then(res => {
        const { applicant } = res.data;

        setData(applicant);
        dispatch(updateApplicantTableStatuses(applicantId, applicant.status));
        setError('');
        if (applicant) outcome = 'ok';
      })
      .catch(err => {
        setError(err);
      })
      .finally(() => {
        setIsFetching(false);
        reportSettled?.(outcome);
      });
  }, [applicantId, dispatch]);

  // The interceptor rejects with the response body's message, so a 403 names
  // the denial instead of the generic error.
  useEffect(() => {
    if (error) {
      dispatch(addErrorMessageFromCaughtValueI18n(error, intl));
    }
  }, [dispatch, error, intl]);

  useEffect(() => {
    if (applicantId && defaultFetching) {
      fetchData();
    }
  }, [applicantId, fetchData, defaultFetching]);

  return { data, error, isFetching, fetchData, setIsFetching };
}
